import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import userD from "../../../assets/customerAssets/user_D.jpeg";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

// ─── Count-up hook ─────────────────────────────────────────────────────────
function useCountUp(target, duration = 1200, delay = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let rafId, startTime = null;
    const tid = setTimeout(() => {
      const step = (ts) => {
        if (!startTime) startTime = ts;
        const p = Math.min((ts - startTime) / duration, 1);
        setValue(Math.floor(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) rafId = requestAnimationFrame(step);
        else setValue(target);
      };
      rafId = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tid); if (rafId) cancelAnimationFrame(rafId); };
  }, [target, duration, delay]);
  return value;
}

// ─── Particle spawner ────────────────────────────────────────────────────────
function spawnParticles({ canvas, containerRef, count = 150, originX, originY, speed = [3.5, 10], decay = [0.006, 0.016] }) {
  const parent = containerRef?.current;
  const W = parent ? parent.offsetWidth  : 700;
  const H = parent ? parent.offsetHeight : 260;
  canvas.width  = W;
  canvas.height = H;

  const cx = originX ?? W / 2;
  const cy = originY ?? H / 2;

  const COLORS = [
    "#FFD700","#FFF176","#FFFFFF","#FFB300",
    "#FFFDE7","#FF8C00","#FFF9C4","#FFEE58",
    "#FFC107","#FFE082","#F9A825","#FFCA28",
  ];

  return Array.from({ length: count }, () => {
    const angle  = Math.random() * Math.PI * 2;
    const spd    = speed[0] + Math.random() * (speed[1] - speed[0]);
    const sz     = 2 + Math.random() * 5;
    const shapeR = Math.random();
    return {
      x: cx, y: cy,
      vx: Math.cos(angle) * spd,
      vy: Math.sin(angle) * spd - Math.random() * 2,
      size: sz,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      alpha: 1,
      decay: decay[0] + Math.random() * (decay[1] - decay[0]),
      shape: shapeR < 0.4 ? "circle" : shapeR < 0.75 ? "square" : "diamond",
      rotate: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.22,
      twinkle: Math.random() > 0.55,
      twinkleSpeed: 0.05 + Math.random() * 0.12,
      twinklePhase: Math.random() * Math.PI * 2,
    };
  });
}

// ─── Canvas particle component ───────────────────────────────────────────────
const BannerParticles = ({ trigger, containerRef, hovered }) => {
  const canvasRef = useRef(null);
  const stateRef  = useRef({ particles: [], rafId: null, running: false, hovering: false });
  const frameRef  = useRef(0);

  const startLoop = useCallback(() => {
    const s = stateRef.current;
    if (s.running) return;
    s.running = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const loop = () => {
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      frameRef.current++;
      s.particles = s.particles.filter(p => p.alpha > 0);
      s.particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        p.vy += 0.09; p.vx *= 0.983; p.vy *= 0.983;
        p.alpha -= p.decay;
        p.rotate += p.rotSpeed;
        const twinkle = p.twinkle
          ? 0.65 + 0.35 * Math.sin(frameRef.current * p.twinkleSpeed + p.twinklePhase) : 1;
        ctx.save();
        ctx.globalAlpha = Math.max(p.alpha * twinkle, 0);
        ctx.fillStyle   = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur  = 5;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotate);
        if (p.shape === "circle") {
          ctx.beginPath(); ctx.arc(0, 0, p.size, 0, Math.PI * 2); ctx.fill();
        } else if (p.shape === "square") {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        } else {
          ctx.beginPath();
          ctx.moveTo(0, -p.size); ctx.lineTo(p.size * 0.6, 0);
          ctx.lineTo(0, p.size); ctx.lineTo(-p.size * 0.6, 0);
          ctx.closePath(); ctx.fill();
        }
        ctx.restore();
      });
      if (s.particles.length > 0 || s.hovering) {
        s.rafId = requestAnimationFrame(loop);
      } else {
        s.running = false;
        ctx.clearRect(0, 0, W, H);
      }
    };
    s.rafId = requestAnimationFrame(loop);
  }, []);

  // One-time burst
  useEffect(() => {
    if (!trigger || !canvasRef.current) return;
    const newP = spawnParticles({ canvas: canvasRef.current, containerRef, count: 150 });
    stateRef.current.particles.push(...newP);
    startLoop();
  }, [trigger]);

  // Hover drizzle
  useEffect(() => {
    const s = stateRef.current;
    s.hovering = hovered;
    if (!hovered) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const interval = setInterval(() => {
      const W = canvas.width  || (containerRef?.current?.offsetWidth  ?? 700);
      const H = canvas.height || (containerRef?.current?.offsetHeight ?? 260);
      const ox = W * 0.1 + Math.random() * W * 0.8;
      const oy = H * 0.1 + Math.random() * H * 0.55;
      const newP = spawnParticles({
        canvas, containerRef, count: 18,
        originX: ox, originY: oy,
        speed: [1.5, 5], decay: [0.012, 0.022],
      });
      s.particles.push(...newP);
      startLoop();
    }, 80);
    return () => { clearInterval(interval); s.hovering = false; };
  }, [hovered, startLoop]);

  useEffect(() => () => {
    const s = stateRef.current;
    if (s.rafId) cancelAnimationFrame(s.rafId);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute", top: 0, left: 0,
        width: "100%", height: "100%",
        pointerEvents: "none", zIndex: 30,
      }}
    />
  );
};

// ─── Sonar rings ─────────────────────────────────────────────────────────────
const SonarRings = ({ visible }) => {
  const rings = [
    { s: 110, bw: 2.5, color: "rgba(255,215,0,0.95)", dur: 2.0, delay: 0,   blur: 0, scale: 2.2 },
    { s: 110, bw: 1.5, color: "rgba(255,215,0,0.55)", dur: 2.0, delay: 0.5, blur: 2, scale: 2.2 },
    { s: 110, bw: 1.5, color: "rgba(255,200,50,0.45)", dur: 2.8, delay: 0.2, blur: 1, scale: 3.2 },
    { s: 110, bw: 1,   color: "rgba(255,200,50,0.25)", dur: 2.8, delay: 0.9, blur: 3, scale: 3.2 },
    { s: 110, bw: 1,   color: "rgba(255,255,180,0.30)", dur: 3.6, delay: 0.7, blur: 4, scale: 4.2 },
    { s: 110, bw: 0.5, color: "rgba(255,255,255,0.15)", dur: 3.6, delay: 1.5, blur: 6, scale: 4.2 },
  ];

  return (
    <>
      {rings.map((r, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: "50%", left: "50%",
            width: `${r.s}px`, height: `${r.s}px`,
            mt: `-${r.s / 2}px`, ml: `-${r.s / 2}px`,
            borderRadius: "50%",
            border: `${r.bw}px solid ${r.color}`,
            filter: r.blur ? `blur(${r.blur}px)` : "none",
            pointerEvents: "none",
            opacity: visible ? 1 : 0,
            zIndex: 5,
            animation: visible
              ? `sonarT_${i} ${r.dur}s cubic-bezier(0.12,0.5,0.3,1) ${r.delay}s infinite`
              : "none",
            [`@keyframes sonarT_${i}`]: {
              "0%":   { transform: "scale(1)", opacity: i % 2 === 0 ? 0.95 : 0.6 },
              "60%":  { opacity: i % 2 === 0 ? 0.2 : 0.1 },
              "100%": { transform: `scale(${r.scale})`, opacity: 0 },
            },
          }}
        />
      ))}
      {/* Breathing halo */}
      {visible && (
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          width: "130px", height: "130px",
          mt: "-65px", ml: "-65px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,215,0,0.18) 0%, rgba(255,215,0,0.06) 50%, transparent 70%)",
          pointerEvents: "none", zIndex: 4,
          animation: "haloBreathT 3s ease-in-out infinite",
          "@keyframes haloBreathT": {
            "0%,100%": { transform: "scale(1)",    opacity: 0.8 },
            "50%":     { transform: "scale(1.18)", opacity: 1   },
          },
        }} />
      )}
    </>
  );
};

// ─── PodiumItem ──────────────────────────────────────────────────────────────
// 2nd → 3rd → 1st entry order to match mobile
const ANIM_DELAY = { 1: 750, 2: 200, 3: 480 };

const PodiumItem = ({ goal, rank, winner, containerRef, containerHovered }) => {
  const [visible, setVisible] = useState(false);
  const [burst,   setBurst]   = useState(false);
  const delay = ANIM_DELAY[rank] ?? 400;

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(true);
      if (winner) setTimeout(() => setBurst(true), 420);
    }, delay);
    return () => clearTimeout(t);
  }, [delay, winner]);

  const animatedVotes = useCountUp(goal?.voteCount || 0, 1200, delay + 300);

  return (
    <Box
      sx={{
        textAlign: "center",
        transform: winner ? "translateY(-25px)" : "none",
        position: "relative",
        overflow: "visible",
      }}
    >
      {/* Sonar rings for 1st place */}
      {winner && <SonarRings visible={visible} />}

      {/* Crown for 1st place */}
      {winner && (
        <Box sx={{
          fontSize: "36px", lineHeight: 1,
          mb: "4px",
          opacity:   visible ? 1 : 0,
          transform: visible
            ? "translateY(0) scale(1)"
            : "translateY(-20px) scale(0.4)",
          transition: "opacity 0.5s ease, transform 0.6s cubic-bezier(0.34,1.5,0.64,1)",
          transitionDelay: "600ms",
          filter: "drop-shadow(0 2px 8px rgba(255,200,0,0.85)) drop-shadow(0 0 16px rgba(255,180,0,0.5))",
          animation: visible ? "crownBobT 3s ease-in-out 1.3s infinite" : "none",
          "@keyframes crownBobT": {
            "0%,100%": { transform: "translateY(0px) rotate(-3deg)"  },
            "50%":     { transform: "translateY(-5px) rotate(3deg)"  },
          },
        }}>
          👑
        </Box>
      )}

      {/* Avatar */}
      <Box
        component="img"
        src={goal?.goal_image || goal?.goalUserDetails?.profile_img || userD}
        alt={goal.goal_scorer}
        sx={{
          width:  winner ? 120 : 95,
          height: winner ? 120 : 95,
          borderRadius: "50%",
          border: "3px solid #FFD700",
          objectFit: "cover",
          display: "block",
          mx: "auto",
          position: "relative",
          zIndex: 6,
          // Zoom-in entry
          opacity:   visible ? 1 : 0,
          transform: visible ? "scale(1)" : "scale(0.1)",
          transition: "opacity 0.5s ease, transform 0.7s cubic-bezier(0.34,1.45,0.64,1)",
        }}
      />

      {/* Rank badge */}
      <Box sx={{
        width: 42, height: 42,
        borderRadius: "20px",
        background: "#fff",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        mx: "auto", mt: -2,
        color: "#F5B400",
        fontWeight: 700,
        position: "relative",
        zIndex: 7,
        opacity:   visible ? 1 : 0,
        transform: visible ? "scale(1)" : "scale(0.5)",
        transition: "opacity 0.35s ease, transform 0.35s cubic-bezier(0.34,1.3,0.64,1)",
        transitionDelay: "200ms",
      }}>
        {rank}
      </Box>

      {/* Name */}
      <Typography sx={{
        color: "#fff", fontWeight: 500, mt: 1, fontSize: "22px",
        opacity:   visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
        transition: "opacity 0.4s ease, transform 0.4s ease",
        transitionDelay: "280ms",
      }}>
        {goal.goal_scorer}
      </Typography>

      {/* Animated vote count */}
      <Typography sx={{
        color: "#fff",
        display: "flex", justifyContent: "center", alignItems: "center",
        gap: 0.5, fontSize: "18px",
        opacity:   visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(6px)",
        transition: "opacity 0.4s ease, transform 0.4s ease",
        transitionDelay: "360ms",
      }}>
        <EmojiEventsIcon fontSize="small" />
        {animatedVotes} Votes
      </Typography>

      {/* Particle burst + hover drizzle (1st place only) */}
      {winner && (
        <BannerParticles
          trigger={burst}
          containerRef={containerRef}
          hovered={containerHovered}
        />
      )}
    </Box>
  );
};

// ─── Podium ──────────────────────────────────────────────────────────────────
const Podium = ({ goals }) => {
  const containerRef    = useRef(null);
  const [hovered, setHovered] = useState(false);

  if (!goals?.length) return null;
  const [first, second, third] = goals;

  return (
    <Box
      ref={containerRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        background: "linear-gradient(90deg, #67D6CB 0%, #7140D4 100%)",
        borderRadius: "8px",
        py: 4, mb: 3,
        position: "relative",
        overflow: "hidden",   // keeps particles inside banner
        cursor: "default",
      }}
    >
      <Typography sx={{
        textAlign: "center", color: "#fff",
        fontSize: { xs: "20px", md: "32px" },
        fontWeight: 700, mb: 4,
      }}>
        TOP LEADERBOARD OF THE MONTH
      </Typography>

      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: { xs: 2, md: 8 },
        // overflow visible so sonar rings aren't clipped at flex-item level
        overflow: "visible",
      }}>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", overflow: "visible" }}>
          {second && (
            <PodiumItem
              goal={second} rank={2}
              containerRef={containerRef}
              containerHovered={hovered}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", overflow: "visible" }}>
          {first && (
            <PodiumItem
              goal={first} rank={1} winner
              containerRef={containerRef}
              containerHovered={hovered}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", overflow: "visible" }}>
          {third && (
            <PodiumItem
              goal={third} rank={3}
              containerRef={containerRef}
              containerHovered={hovered}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Podium;